import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Skeleton,
  Card,
  CardContent,
  Grid,
  TextField,
} from '@mui/material';
import { Refresh, TrendingUp } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

interface SPCSubgroup {
  index: number;
  mean: number;
  range: number;
  min: number;
  max: number;
  sampleSize: number;
  timestamp: string;
}

interface SPCData {
  parameterName: string;
  subgroupSize: number;
  subgroups: SPCSubgroup[];
  xBarChart: { centerLine: number; ucl: number; lcl: number };
  rChart: { centerLine: number; ucl: number; lcl: number };
  processCapability: { cp: number; cpk: number } | null;
}

export default function SPCCharts() {
  const [parameterName, setParameterName] = useState('weight');
  const [subgroupSize, setSubgroupSize] = useState('5');
  const [spcData, setSpcData] = useState<SPCData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSPC = useCallback(async () => {
    if (!parameterName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}/analytics/metrics/spc`, {
        params: {
          parameterName: parameterName.trim(),
          subgroupSize: parseInt(subgroupSize, 10) || 5,
          limit: 30,
        },
      });
      const data = res.data?.data;
      if (data) {
        setSpcData(data);
      } else {
        setError('No SPC data returned');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to fetch SPC data');
    } finally {
      setLoading(false);
    }
  }, [parameterName, subgroupSize]);

  const cpkColor = (cpk: number) => {
    if (cpk >= 1.33) return 'success.main';
    if (cpk >= 1.0) return 'warning.main';
    return 'error.main';
  };

  const cpkLabel = (cpk: number) => {
    if (cpk >= 1.33) return 'Capable';
    if (cpk >= 1.0) return 'Marginal';
    return 'Not Capable';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            SPC Control Charts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Statistical Process Control -- X-bar and R charts with process capability analysis.
          </Typography>
        </Box>
      </Box>

      {/* Input Controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Quality Parameter"
              fullWidth
              value={parameterName}
              onChange={(e) => setParameterName(e.target.value)}
              placeholder="e.g., weight, temperature, moisture"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Subgroup Size"
              type="number"
              fullWidth
              value={subgroupSize}
              onChange={(e) => setSubgroupSize(e.target.value)}
              inputProps={{ min: 2, max: 10 }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              startIcon={<TrendingUp />}
              onClick={fetchSPC}
              disabled={loading || !parameterName.trim()}
              fullWidth
            >
              {loading ? 'Calculating...' : 'Generate Charts'}
            </Button>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchSPC}
              disabled={loading}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box>
          <Skeleton variant="rectangular" height={300} sx={{ mb: 2, borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
        </Box>
      )}

      {spcData && !loading && (
        <>
          {/* Process Capability Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color="text.secondary">Parameter</Typography>
                  <Typography variant="h6">{spcData.parameterName}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" color="text.secondary">Subgroups</Typography>
                  <Typography variant="h6">{spcData.subgroups.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            {spcData.processCapability && (
              <>
                <Grid item xs={12} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="text.secondary">Cp</Typography>
                      <Typography variant="h6">{spcData.processCapability.cp.toFixed(2)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="text.secondary">
                        Cpk ({cpkLabel(spcData.processCapability.cpk)})
                      </Typography>
                      <Typography variant="h6" sx={{ color: cpkColor(spcData.processCapability.cpk) }}>
                        {spcData.processCapability.cpk.toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>

          {spcData.subgroups.length === 0 ? (
            <Alert severity="info">
              No measurement data found for parameter "{spcData.parameterName}". Record quality checks with this parameter name to generate SPC charts.
            </Alert>
          ) : (
            <>
              {/* X-bar Chart */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  X-bar Chart (Subgroup Means)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Center Line: {spcData.xBarChart.centerLine.toFixed(4)} | UCL: {spcData.xBarChart.ucl.toFixed(4)} | LCL: {spcData.xBarChart.lcl.toFixed(4)}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={spcData.subgroups} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="index" label={{ value: 'Subgroup', position: 'insideBottom', offset: -5 }} />
                    <YAxis domain={['auto', 'auto']} />
                    <Tooltip
                      formatter={(value: number) => value.toFixed(4)}
                      labelFormatter={(label) => `Subgroup ${label}`}
                    />
                    <Legend />
                    <ReferenceLine y={spcData.xBarChart.centerLine} stroke="#1976d2" strokeDasharray="5 5" label="CL" />
                    <ReferenceLine y={spcData.xBarChart.ucl} stroke="#d32f2f" strokeDasharray="3 3" label="UCL" />
                    <ReferenceLine y={spcData.xBarChart.lcl} stroke="#d32f2f" strokeDasharray="3 3" label="LCL" />
                    <Line
                      type="monotone"
                      dataKey="mean"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ r: 4, fill: '#1976d2' }}
                      activeDot={{ r: 6 }}
                      name="X-bar (Mean)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>

              {/* R Chart */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  R Chart (Subgroup Ranges)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Center Line: {spcData.rChart.centerLine.toFixed(4)} | UCL: {spcData.rChart.ucl.toFixed(4)} | LCL: {spcData.rChart.lcl.toFixed(4)}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={spcData.subgroups} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="index" label={{ value: 'Subgroup', position: 'insideBottom', offset: -5 }} />
                    <YAxis domain={[0, 'auto']} />
                    <Tooltip
                      formatter={(value: number) => value.toFixed(4)}
                      labelFormatter={(label) => `Subgroup ${label}`}
                    />
                    <Legend />
                    <ReferenceLine y={spcData.rChart.centerLine} stroke="#388e3c" strokeDasharray="5 5" label="CL" />
                    <ReferenceLine y={spcData.rChart.ucl} stroke="#d32f2f" strokeDasharray="3 3" label="UCL" />
                    {spcData.rChart.lcl > 0 && (
                      <ReferenceLine y={spcData.rChart.lcl} stroke="#d32f2f" strokeDasharray="3 3" label="LCL" />
                    )}
                    <Line
                      type="monotone"
                      dataKey="range"
                      stroke="#388e3c"
                      strokeWidth={2}
                      dot={{ r: 4, fill: '#388e3c' }}
                      activeDot={{ r: 6 }}
                      name="Range"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </>
          )}
        </>
      )}
    </Box>
  );
}
